import api from '@/helpers/api';

import type {
	ParsedResume,
	ParseResumeData,
	UploadDocumentPayload,
	WorkerDocument,
	WorkerDocumentRejectData,
	WorkerDocumentValidateData,
} from '@/modules/worker/services/documentTypes';

const getDocuments = async (workerId: string): Promise<WorkerDocument[]> => {
	const response = await api.request({
		url: `/workers/${workerId}/documents`,
		method: 'GET',
	});
	return response.data;
};

const uploadDocument = (workerId: string, payload: UploadDocumentPayload) => {
	return api.request({
		url: `/workers/${workerId}/documents`,
		method: 'POST',
		data: payload,
	});
};

const updateDocument = (
	workerId: string,
	documentId: string,
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	payload: Record<string, any>
) => {
	return api.request({
		url: `/workers/${workerId}/documents/${documentId}`,
		method: 'PATCH',
		data: payload,
	});
};

const deleteDocument = (workerId: string, documentId: string) => {
	return api.request({
		url: `/workers/${workerId}/documents/${documentId}`,
		method: 'DELETE',
	});
};

const verifyDocument = (workerId: string, documentId: string) => {
	return api.request({
		url: `/workers/${workerId}/documents/${documentId}/commands/verify`,
		method: 'POST',
	});
};

const validateDocument = (
	workerId: string,
	documentId: string,
	data: WorkerDocumentValidateData
) => {
	return api.request({
		url: `/workers/${workerId}/documents/${documentId}/validate`,
		method: 'POST',
		data,
	});
};

const rejectDocument = (
	workerId: string,
	documentId: string,
	data: WorkerDocumentRejectData
) => {
	return api.request({
		url: `/workers/${workerId}/documents/${documentId}/reject`,
		method: 'POST',
		data,
	});
};

const extractOcr = (
	workerId: string,
	payload: {
		fileBase64: string;
		ocrDocumentType: string;
		ocrFieldMapping: Record<string, string>;
		fileName?: string;
	}
) => {
	return api.request<{
		success: boolean;
		extractedFields: Record<string, string | string[] | null>;
		confidence?: number;
		error?: string;
	}>({
		url: `/workers/${workerId}/documents/extract-ocr`,
		method: 'POST',
		data: payload,
	});
};

const parseResume = async (data: ParseResumeData): Promise<ParsedResume> => {
	const response = await api.request({
		url: '/workers/documents/cv-parser',
		method: 'POST',
		data,
	});
	return response.data;
};

export default {
	getDocuments,
	uploadDocument,
	updateDocument,
	deleteDocument,
	verifyDocument,
	validateDocument,
	rejectDocument,
	extractOcr,
	parseResume,
};
